import { useEffect, useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';
import { ErrorState, LoadingState } from '@/shared/ui';

import {
  useAssignStoreLocationToRegion,
  useCreateStoreLocation,
  useCreateStoreRegion,
  useDeleteStoreLocation,
  useDeleteStoreRegion,
  useRemoveStoreLocationFromRegion,
  useStoreLocations,
  useStoreLocationsByRegion,
  useStoreRegions,
  useStoreServiceSettings,
  useToggleStoreLocation,
  useToggleStoreRegion,
  useUpdateStoreLocation,
  useUpdateStoreRegion,
  useUpdateStoreServiceSettings,
} from '../../hooks';
import { useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type {
  CreateStoreLocationRequest,
  CreateStoreRegionRequest,
  StoreBusinessHours,
  StoreDayOfWeek,
  StoreLocationContact,
  StoreLocationResponse,
  StoreLocationServiceType,
  StoreRegionResponse,
  StoreRegionType,
  StoreServiceSettingsResponse,
  UpdateStoreLocationRequest,
  UpdateStoreRegionRequest,
} from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  FieldRow,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';
import {
  STORE_DAY_OF_WEEK_OPTIONS,
  STORE_REGION_TYPE_OPTIONS,
  STORE_SERVICE_LOCATION_TYPE_OPTIONS,
} from '../options';

type StoreTab = 'locations' | 'regions' | 'assignments' | 'settings';

const LOCATION_EMPTY: CreateStoreLocationRequest = {
  code: '',
  name: '',
  type: 'STORE',
  address: '',
  city: '',
  latitude: null,
  longitude: null,
  phone: '',
  email: '',
  active: true,
  businessHours: [],
  contacts: [],
};

const REGION_EMPTY: CreateStoreRegionRequest = {
  code: '',
  name: '',
  type: 'CITY',
  active: true,
  defaultRegion: false,
};

function cleanString(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

function parseNumber(value: string): number | null {
  if (value.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * Normalizes a location's `businessHours` to a fixed 7-entry array, Monday→Sunday,
 * so the editor always has a row per day and every row's required fields
 * (`dayOfWeek`/`openTime`/`closeTime`) stay populated. Days missing from the source
 * default to closed (`active: false`) with a sensible 09:00–21:00 fallback window.
 *
 * Source entries are matched by `dayOfWeek`, not array position, so a partial or
 * out-of-order array from the server is handled correctly. If the source ever
 * contains more than one entry for the same day (or an entry whose `dayOfWeek`
 * isn't one of the 7 canonical values), only the first matching entry per day is
 * kept and the rest are silently dropped on the next save. This editor itself can
 * never produce such data — it's only reachable via a non-UI write (direct API/DB
 * access or a migration artifact) — but it's worth knowing before debugging a
 * "my extra business-hours row disappeared" report.
 */
function normalizeBusinessHours(hours: StoreBusinessHours[] | undefined): StoreBusinessHours[] {
  return STORE_DAY_OF_WEEK_OPTIONS.map(({ value: dayOfWeek }) => {
    const existing = hours?.find((entry) => entry.dayOfWeek === dayOfWeek);
    return existing ?? { dayOfWeek, openTime: '09:00', closeTime: '21:00', active: false };
  });
}

function activeChip(active: boolean) {
  return (
    <Chip
      size="small"
      label={active ? 'Active' : 'Inactive'}
      color={active ? 'success' : 'default'}
      variant={active ? 'filled' : 'outlined'}
    />
  );
}

/** Fixed 7-row (Monday→Sunday) business-hours editor for {@link LocationDialog}. */
function LocationBusinessHoursEditor({
  value,
  onChange,
  disabled,
}: {
  value: StoreBusinessHours[];
  onChange: (value: StoreBusinessHours[]) => void;
  disabled: boolean;
}) {
  // Re-normalize defensively rather than trusting `value` is already a full 7-entry
  // array: this keeps the editor correct regardless of the parent's effect timing,
  // and means `setDay` always has all 7 days to map over.
  const rows = useMemo(() => normalizeBusinessHours(value), [value]);

  const setDay = (day: StoreDayOfWeek, patch: Partial<StoreBusinessHours>) => {
    onChange(rows.map((entry) => (entry.dayOfWeek === day ? { ...entry, ...patch } : entry)));
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Business hours
      </Typography>
      <Stack spacing={0.5}>
        {STORE_DAY_OF_WEEK_OPTIONS.map((option) => {
          const hours = rows.find((entry) => entry.dayOfWeek === option.value);
          const dayActive = hours?.active ?? false;
          return (
            <Box
              key={option.value}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '120px auto 1fr 1fr' },
                gap: { xs: 1, sm: 2 },
                alignItems: 'center',
                py: 0.75,
              }}
            >
              <Typography variant="body2">{option.label}</Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={dayActive}
                    disabled={disabled}
                    onChange={(event) => setDay(option.value, { active: event.target.checked })}
                    slotProps={{ input: { 'aria-label': `${option.label} open` } }}
                  />
                }
                label="Open"
              />
              <TextField
                label="Opens"
                type="time"
                size="small"
                value={hours?.openTime ?? '09:00'}
                disabled={disabled || !dayActive}
                onChange={(event) => setDay(option.value, { openTime: event.target.value })}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { 'aria-label': `${option.label} opening time` },
                }}
              />
              <TextField
                label="Closes"
                type="time"
                size="small"
                value={hours?.closeTime ?? '21:00'}
                disabled={disabled || !dayActive}
                onChange={(event) => setDay(option.value, { closeTime: event.target.value })}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { 'aria-label': `${option.label} closing time` },
                }}
              />
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

/** Dynamic add/remove editor for a location's additional {@link StoreLocationContact} entries. */
function LocationContactsEditor({
  value,
  onChange,
  disabled,
}: {
  value: StoreLocationContact[];
  onChange: (value: StoreLocationContact[]) => void;
  disabled: boolean;
}) {
  const updateContact = (index: number, patch: Partial<StoreLocationContact>) => {
    onChange(value.map((contact, current) => (current === index ? { ...contact, ...patch } : contact)));
  };

  const removeContact = (index: number) => {
    onChange(value.filter((_, current) => current !== index));
  };

  const addContact = () => {
    onChange([...value, { phone: '', email: '', website: '' }]);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Contacts
      </Typography>
      {value.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No additional contacts yet.
        </Typography>
      ) : (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1.5 }}>
          <Stack divider={<Divider />}>
            {value.map((contact, index) => (
              <Stack key={index} direction="row" spacing={1} alignItems="flex-start" sx={{ p: 2 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Contact {index + 1}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
                    <TextField
                      label="Phone"
                      size="small"
                      value={contact.phone ?? ''}
                      disabled={disabled}
                      onChange={(event) => updateContact(index, { phone: event.target.value })}
                      slotProps={{ htmlInput: { 'aria-label': `Contact ${index + 1} phone` } }}
                    />
                    <TextField
                      label="Email"
                      type="email"
                      size="small"
                      value={contact.email ?? ''}
                      disabled={disabled}
                      onChange={(event) => updateContact(index, { email: event.target.value })}
                      slotProps={{ htmlInput: { 'aria-label': `Contact ${index + 1} email` } }}
                    />
                    <TextField
                      label="Website"
                      type="url"
                      size="small"
                      value={contact.website ?? ''}
                      disabled={disabled}
                      onChange={(event) => updateContact(index, { website: event.target.value })}
                      slotProps={{ htmlInput: { 'aria-label': `Contact ${index + 1} website` } }}
                    />
                  </Box>
                </Box>
                <IconButton
                  aria-label={`Remove contact ${index + 1}`}
                  color="error"
                  size="small"
                  disabled={disabled}
                  onClick={() => removeContact(index)}
                >
                  <DeleteOutlineRoundedIcon />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
      <Button startIcon={<AddRoundedIcon />} onClick={addContact} disabled={disabled} size="small">
        Add contact
      </Button>
    </Box>
  );
}

function LocationDialog({
  open,
  value,
  onClose,
  onSubmit,
  disabled,
}: {
  open: boolean;
  value: StoreLocationResponse | null;
  onClose: () => void;
  onSubmit: (body: CreateStoreLocationRequest | UpdateStoreLocationRequest) => void;
  disabled: boolean;
}) {
  const [draft, setDraft] = useState<CreateStoreLocationRequest>(LOCATION_EMPTY);

  useEffect(() => {
    if (open) {
      setDraft(
        value
          ? {
              code: value.code,
              name: value.name,
              type: value.type,
              addressId: value.addressId ?? null,
              address: value.address ?? '',
              city: value.city ?? '',
              latitude: value.latitude ?? null,
              longitude: value.longitude ?? null,
              phone: value.phone ?? '',
              email: value.email ?? '',
              active: value.active,
              businessHours: normalizeBusinessHours(value.businessHours),
              contacts: value.contacts ?? [],
            }
          : { ...LOCATION_EMPTY, businessHours: normalizeBusinessHours(undefined) },
      );
    }
  }, [open, value]);

  const set = <K extends keyof CreateStoreLocationRequest>(key: K, next: CreateStoreLocationRequest[K]) =>
    setDraft((current) => ({ ...current, [key]: next }));

  const submit = () => {
    // Every business-hours row already carries valid required fields (see
    // `normalizeBusinessHours`), so the full 7-entry array passes through as-is.
    // Contacts are trimmed and rows left entirely blank (added, then abandoned) are
    // dropped so they aren't persisted as empty `{}` entries.
    const cleanedContacts = (draft.contacts ?? [])
      .map((contact) => ({
        phone: cleanString(contact.phone),
        email: cleanString(contact.email),
        website: cleanString(contact.website),
      }))
      .filter((contact) => contact.phone || contact.email || contact.website);

    const common = {
      name: draft.name.trim(),
      type: draft.type,
      addressId: cleanString(draft.addressId),
      address: cleanString(draft.address),
      city: cleanString(draft.city),
      latitude: draft.latitude ?? null,
      longitude: draft.longitude ?? null,
      phone: cleanString(draft.phone),
      email: cleanString(draft.email),
      businessHours: draft.businessHours ?? [],
      contacts: cleanedContacts,
    };
    if (value) {
      onSubmit(common);
    } else {
      onSubmit({ ...common, code: draft.code.trim(), active: draft.active ?? true });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{value ? 'Edit location' : 'Create location'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
          <TextField
            label="Code"
            value={draft.code}
            disabled={disabled || Boolean(value)}
            onChange={(event) => set('code', event.target.value)}
            required
            size="small"
          />
          <TextField
            label="Name"
            value={draft.name}
            disabled={disabled}
            onChange={(event) => set('name', event.target.value)}
            required
            size="small"
          />
          <TextField
            select
            label="Type"
            value={draft.type}
            disabled={disabled}
            onChange={(event) => set('type', event.target.value as StoreLocationServiceType)}
            size="small"
          >
            {STORE_SERVICE_LOCATION_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="City"
            value={draft.city ?? ''}
            disabled={disabled}
            onChange={(event) => set('city', event.target.value)}
            size="small"
          />
          <TextField
            label="Address"
            value={draft.address ?? ''}
            disabled={disabled}
            onChange={(event) => set('address', event.target.value)}
            size="small"
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />
          <TextField
            label="Phone"
            value={draft.phone ?? ''}
            disabled={disabled}
            onChange={(event) => set('phone', event.target.value)}
            size="small"
          />
          <TextField
            label="Email"
            value={draft.email ?? ''}
            disabled={disabled}
            onChange={(event) => set('email', event.target.value)}
            size="small"
            type="email"
          />
          <TextField
            label="Latitude"
            value={draft.latitude ?? ''}
            disabled={disabled}
            onChange={(event) => set('latitude', parseNumber(event.target.value))}
            size="small"
          />
          <TextField
            label="Longitude"
            value={draft.longitude ?? ''}
            disabled={disabled}
            onChange={(event) => set('longitude', parseNumber(event.target.value))}
            size="small"
          />
          {!value ? (
            <FormControlLabel
              control={
                <Switch
                  checked={draft.active ?? true}
                  disabled={disabled}
                  onChange={(event) => set('active', event.target.checked)}
                />
              }
              label="Active"
            />
          ) : null}
        </Box>
        <LocationBusinessHoursEditor
          value={draft.businessHours ?? []}
          onChange={(next) => set('businessHours', next)}
          disabled={disabled}
        />
        <LocationContactsEditor
          value={draft.contacts ?? []}
          onChange={(next) => set('contacts', next)}
          disabled={disabled}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={disabled || !draft.code.trim() || !draft.name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RegionDialog({
  open,
  value,
  onClose,
  onSubmit,
  disabled,
}: {
  open: boolean;
  value: StoreRegionResponse | null;
  onClose: () => void;
  onSubmit: (body: CreateStoreRegionRequest | UpdateStoreRegionRequest) => void;
  disabled: boolean;
}) {
  const [draft, setDraft] = useState<CreateStoreRegionRequest>(REGION_EMPTY);

  useEffect(() => {
    if (open) {
      setDraft(
        value
          ? {
              code: value.code,
              name: value.name,
              type: value.type,
              active: value.active,
              defaultRegion: value.defaultRegion,
            }
          : REGION_EMPTY,
      );
    }
  }, [open, value]);

  const set = <K extends keyof CreateStoreRegionRequest>(key: K, next: CreateStoreRegionRequest[K]) =>
    setDraft((current) => ({ ...current, [key]: next }));

  const submit = () => {
    const common = { name: draft.name.trim(), type: draft.type, defaultRegion: draft.defaultRegion ?? false };
    onSubmit(value ? common : { ...common, code: draft.code.trim(), active: draft.active ?? true });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{value ? 'Edit region' : 'Create region'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Code"
            value={draft.code}
            disabled={disabled || Boolean(value)}
            onChange={(event) => set('code', event.target.value)}
            required
            size="small"
          />
          <TextField
            label="Name"
            value={draft.name}
            disabled={disabled}
            onChange={(event) => set('name', event.target.value)}
            required
            size="small"
          />
          <TextField
            select
            label="Type"
            value={draft.type}
            disabled={disabled}
            onChange={(event) => set('type', event.target.value as StoreRegionType)}
            size="small"
          >
            {STORE_REGION_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={draft.defaultRegion ?? false}
                disabled={disabled}
                onChange={(event) => set('defaultRegion', event.target.checked)}
              />
            }
            label="Default region"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={disabled || !draft.code.trim() || !draft.name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LocationsTab({
  canManage,
  notify,
  notifyError,
}: {
  canManage: boolean;
  notify: (message: string) => void;
  notifyError: (message: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<StoreLocationResponse | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const params = useMemo(() => ({ query: query.trim() || undefined, page: 0, size: 100 }), [query]);
  const locations = useStoreLocations(params);
  const create = useCreateStoreLocation();
  const update = useUpdateStoreLocation();
  const remove = useDeleteStoreLocation();
  const toggle = useToggleStoreLocation();
  const busy = create.isPending || update.isPending || remove.isPending || toggle.isPending;

  const save = (body: CreateStoreLocationRequest | UpdateStoreLocationRequest) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: body as UpdateStoreLocationRequest },
        {
          onSuccess: () => (setDialogOpen(false), notify('Location saved.')),
          onError: (error: NormalizedApiError) => notifyError(error.message),
        },
      );
    } else {
      create.mutate(body as CreateStoreLocationRequest, {
        onSuccess: () => (setDialogOpen(false), notify('Location created.')),
        onError: (error: NormalizedApiError) => notifyError(error.message),
      });
    }
  };

  return (
    <SettingsCard
      title="Locations"
      description="Stores, warehouses, showrooms, dark stores and pickup points."
      icon={<PlaceRoundedIcon />}
      action={
        canManage ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => (setEditing(null), setDialogOpen(true))}
          >
            Create location
          </Button>
        ) : undefined
      }
      divided={false}
    >
      <Stack spacing={2} sx={{ pt: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Name, code, city or address"
        />
        {locations.isLoading ? <LoadingState label="Loading locations…" /> : null}
        {locations.isError ? (
          <ErrorState title="Couldn't load locations" message={locations.error.message} onRetry={() => void locations.refetch()} />
        ) : null}
        {locations.isSuccess ? (
          locations.data.items.length === 0 ? (
            <Alert severity="info" variant="outlined">No locations found.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Location</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.data.items.map((location) => (
                  <TableRow key={location.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2">{location.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {location.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{location.type}</TableCell>
                    <TableCell>{location.city || '—'}</TableCell>
                    <TableCell>{activeChip(location.active)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={location.active ? 'Deactivate' : 'Activate'}>
                        <span>
                          <Switch
                            size="small"
                            checked={location.active}
                            disabled={!canManage || busy}
                            onChange={() =>
                              toggle.mutate(
                                { id: location.id, active: location.active },
                                { onError: (error: NormalizedApiError) => notifyError(error.message) },
                              )
                            }
                          />
                        </span>
                      </Tooltip>
                      <IconButton
                        aria-label="Edit location"
                        disabled={!canManage || busy}
                        onClick={() => (setEditing(location), setDialogOpen(true))}
                      >
                        <EditRoundedIcon />
                      </IconButton>
                      <IconButton
                        aria-label="Delete location"
                        color="error"
                        disabled={!canManage || busy}
                        onClick={() =>
                          remove.mutate(location.id, {
                            onSuccess: () => notify('Location deleted.'),
                            onError: (error: NormalizedApiError) => notifyError(error.message),
                          })
                        }
                      >
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : null}
      </Stack>
      <LocationDialog open={dialogOpen} value={editing} onClose={() => setDialogOpen(false)} onSubmit={save} disabled={!canManage || busy} />
    </SettingsCard>
  );
}

function RegionsTab({
  canManage,
  notify,
  notifyError,
}: {
  canManage: boolean;
  notify: (message: string) => void;
  notifyError: (message: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<StoreRegionResponse | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const params = useMemo(() => ({ query: query.trim() || undefined, page: 0, size: 100 }), [query]);
  const regions = useStoreRegions(params);
  const create = useCreateStoreRegion();
  const update = useUpdateStoreRegion();
  const remove = useDeleteStoreRegion();
  const toggle = useToggleStoreRegion();
  const busy = create.isPending || update.isPending || remove.isPending || toggle.isPending;

  const save = (body: CreateStoreRegionRequest | UpdateStoreRegionRequest) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: body as UpdateStoreRegionRequest },
        {
          onSuccess: () => (setDialogOpen(false), notify('Region saved.')),
          onError: (error: NormalizedApiError) => notifyError(error.message),
        },
      );
    } else {
      create.mutate(body as CreateStoreRegionRequest, {
        onSuccess: () => (setDialogOpen(false), notify('Region created.')),
        onError: (error: NormalizedApiError) => notifyError(error.message),
      });
    }
  };

  return (
    <SettingsCard
      title="Regions"
      description="Service regions used to group fulfilment locations."
      icon={<StoreRoundedIcon />}
      action={
        canManage ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => (setEditing(null), setDialogOpen(true))}
          >
            Create region
          </Button>
        ) : undefined
      }
      divided={false}
    >
      <Stack spacing={2} sx={{ pt: 2 }}>
        <TextField size="small" label="Search" value={query} onChange={(event) => setQuery(event.target.value)} />
        {regions.isLoading ? <LoadingState label="Loading regions…" /> : null}
        {regions.isError ? (
          <ErrorState title="Couldn't load regions" message={regions.error.message} onRetry={() => void regions.refetch()} />
        ) : null}
        {regions.isSuccess ? (
          regions.data.items.length === 0 ? (
            <Alert severity="info" variant="outlined">No regions found.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Region</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {regions.data.items.map((region) => (
                  <TableRow key={region.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2">{region.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {region.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{region.type}</TableCell>
                    <TableCell>{region.defaultRegion ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{activeChip(region.active)}</TableCell>
                    <TableCell align="right">
                      <Switch
                        size="small"
                        checked={region.active}
                        disabled={!canManage || busy}
                        onChange={() =>
                          toggle.mutate(
                            { id: region.id, active: region.active },
                            { onError: (error: NormalizedApiError) => notifyError(error.message) },
                          )
                        }
                      />
                      <IconButton
                        aria-label="Edit region"
                        disabled={!canManage || busy}
                        onClick={() => (setEditing(region), setDialogOpen(true))}
                      >
                        <EditRoundedIcon />
                      </IconButton>
                      <IconButton
                        aria-label="Delete region"
                        color="error"
                        disabled={!canManage || busy}
                        onClick={() =>
                          remove.mutate(region.id, {
                            onSuccess: () => notify('Region deleted.'),
                            onError: (error: NormalizedApiError) => notifyError(error.message),
                          })
                        }
                      >
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : null}
      </Stack>
      <RegionDialog open={dialogOpen} value={editing} onClose={() => setDialogOpen(false)} onSubmit={save} disabled={!canManage || busy} />
    </SettingsCard>
  );
}

function AssignmentsTab({
  canManage,
  notify,
  notifyError,
}: {
  canManage: boolean;
  notify: (message: string) => void;
  notifyError: (message: string) => void;
}) {
  const locations = useStoreLocations({ page: 0, size: 100 });
  const regions = useStoreRegions({ page: 0, size: 100 });
  const [regionId, setRegionId] = useState('');
  const [locationId, setLocationId] = useState('');
  const assigned = useStoreLocationsByRegion(regionId || null);
  const assign = useAssignStoreLocationToRegion();
  const remove = useRemoveStoreLocationFromRegion();
  const regionOptions = (regions.data?.items ?? []).map((r) => ({ value: r.id, label: `${r.name} (${r.code})` }));
  const locationOptions = (locations.data?.items ?? []).map((l) => ({ value: l.id, label: `${l.name} (${l.code})` }));
  const busy = assign.isPending || remove.isPending;

  return (
    <SettingsCard
      title="Assignments"
      description="Connect regions to locations. Assignment is idempotent and managed by web-store-service."
      icon={<LinkRoundedIcon />}
      divided={false}
    >
      <Stack spacing={2} sx={{ pt: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' }, gap: 2 }}>
          <TextField select size="small" label="Region" value={regionId} onChange={(e) => setRegionId(e.target.value)}>
            {regionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Location" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {locationOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            disabled={!canManage || !regionId || !locationId || busy}
            onClick={() =>
              assign.mutate(
                { regionId, locationId },
                {
                  onSuccess: () => notify('Assignment saved.'),
                  onError: (error: NormalizedApiError) => notifyError(error.message),
                },
              )
            }
          >
            Assign
          </Button>
        </Box>
        {assigned.isLoading ? <LoadingState label="Loading assignments…" /> : null}
        {assigned.isError ? <ErrorState title="Couldn't load assignments" message={assigned.error.message} /> : null}
        {assigned.isSuccess && regionId ? (
          assigned.data.items.length === 0 ? (
            <Alert severity="info" variant="outlined">No locations assigned to this region.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Assigned location</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assigned.data.items.map((location) => (
                  <TableRow key={location.id}>
                    <TableCell>{location.name}</TableCell>
                    <TableCell>{location.type}</TableCell>
                    <TableCell>{activeChip(location.active)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label="Remove assignment"
                        color="error"
                        disabled={!canManage || busy}
                        onClick={() =>
                          remove.mutate(
                            { regionId, locationId: location.id },
                            {
                              onSuccess: () => notify('Assignment removed.'),
                              onError: (error: NormalizedApiError) => notifyError(error.message),
                            },
                          )
                        }
                      >
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : null}
      </Stack>
    </SettingsCard>
  );
}

function StoreServiceSettingsTab({
  settings,
  locations,
  canManage,
  onSaved,
  onError,
}: {
  settings: StoreServiceSettingsResponse;
  locations: StoreLocationResponse[];
  canManage: boolean;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}) {
  const form = useSettingsForm(settings);
  const update = useUpdateStoreServiceSettings();
  const draft = form.draft;
  if (!draft) return null;
  const locationOptions = locations.map((location) => ({ value: location.id, label: `${location.name} (${location.code})` }));
  const disabled = !canManage || update.isPending;

  const save = () => {
    update.mutate(
      {
        expectedVersion: draft.version,
        defaultPickupEnabled: draft.defaultPickupEnabled,
        defaultLocationId: draft.defaultLocationId ?? null,
        pickupInstructions: draft.pickupInstructions ?? null,
        showLocationsOnStorefront: draft.showLocationsOnStorefront,
      },
      {
        onSuccess: () => onSaved('Store settings saved.'),
        onError: (error: NormalizedApiError) => onError(error.message),
      },
    );
  };

  return (
    <SettingsCard
      title="Store settings"
      description="Pickup defaults and storefront visibility for locations."
      icon={<SettingsRoundedIcon />}
      action={
        <Button variant="contained" disabled={!form.dirty || disabled} onClick={save}>
          Save settings
        </Button>
      }
    >
      <SwitchField
        label="Default pickup"
        description="Enable pickup as the default fulfilment mode."
        checked={draft.defaultPickupEnabled}
        onChange={(value) => form.setField('defaultPickupEnabled', value)}
        disabled={disabled}
      />
      <SwitchField
        label="Show locations on storefront"
        description="Expose the active location list to customers."
        checked={draft.showLocationsOnStorefront}
        onChange={(value) => form.setField('showLocationsOnStorefront', value)}
        disabled={disabled}
      />
      <SelectField
        label="Default location"
        description="Preferred pickup location."
        value={draft.defaultLocationId ?? ''}
        options={locationOptions}
        onChange={(value) => form.setField('defaultLocationId', value || null)}
        disabled={disabled}
        allowEmpty
        emptyLabel="None"
      />
      <TextInputField
        label="Pickup instructions"
        description="Shown to customers when pickup is available."
        value={draft.pickupInstructions ?? ''}
        onChange={(value) => form.setField('pickupInstructions', value || null)}
        disabled={disabled}
        multiline
        rows={3}
      />
      <FieldRow label="Version" description="Used for optimistic locking.">
        <Chip size="small" variant="outlined" label={`Version ${draft.version}`} />
      </FieldRow>
    </SettingsCard>
  );
}

/** Store domain management: locations, regions, assignments and store-service settings. */
export function StoreProfileSettingsPage() {
  const { canView, canManage } = useDomainPermissions('store');
  const [tab, setTab] = useState<StoreTab>('locations');
  const settings = useStoreServiceSettings();
  const allLocations = useStoreLocations({ page: 0, size: 100 });
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const notify = (message: string) => success(message);
  const audit = settings.data
    ? { version: settings.data.version, updatedAt: settings.data.updatedAt, updatedBy: null }
    : null;

  return (
    <SettingsScaffold
      title="Store domain"
      subtitle="Regions, fulfilment locations, assignments and pickup settings mastered by web-store-service."
      icon={<StoreRoundedIcon />}
      query={settings}
      canView={canView}
      canManage={canManage}
      audit={audit}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {(data) => {
        const locations = allLocations.data?.items ?? [];
        return (
          <Stack spacing={2.5}>
            {!canManage ? (
              <Alert severity="info" variant="outlined">
                You can view this domain, but editing actions are disabled for your role.
              </Alert>
            ) : null}
            <Tabs value={tab} onChange={(_event, value: StoreTab) => setTab(value)} variant="scrollable">
              <Tab value="locations" label="Locations" />
              <Tab value="regions" label="Regions" />
              <Tab value="assignments" label="Assignments" />
              <Tab value="settings" label="Settings" />
            </Tabs>
            {tab === 'locations' ? <LocationsTab canManage={canManage} notify={notify} notifyError={notifyError} /> : null}
            {tab === 'regions' ? <RegionsTab canManage={canManage} notify={notify} notifyError={notifyError} /> : null}
            {tab === 'assignments' ? <AssignmentsTab canManage={canManage} notify={notify} notifyError={notifyError} /> : null}
            {tab === 'settings' ? (
              <StoreServiceSettingsTab
                settings={data}
                locations={locations}
                canManage={canManage}
                onSaved={notify}
                onError={notifyError}
              />
            ) : null}
            {allLocations.isError ? (
              <Alert severity="warning" variant="outlined">
                {allLocations.error.message}
              </Alert>
            ) : null}
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
